import {
	API_ENDPOINTS,
	Badge,
	DataTable,
	formatDate,
	Icon,
	Icons,
	Roles,
	route,
	StringHelpers,
	useAuthorisationContext,
	useDisabledContext,
	useLangContext,
	Variants,
	customToast,
	checkTheme,
	Themes,
} from 'carrier-fe';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { useState } from 'react';

type CourseType = {
	id: string;
	individual_training_course_id: string;
	name: string;
	category_name: string;
	due_at: string;
	admin_status: string;
	status_at: string;
	permissions: {
		view: boolean;
		update: boolean;
		delete: boolean;
		restore: boolean;
	};
};

type DueDateCellProps = {
	course: CourseType;
	canEditDueDate: boolean;
	userId: string;
	onSaveSuccess: () => void;
};

function DueDateCell({ course, canEditDueDate, userId, onSaveSuccess }: DueDateCellProps) {
	const [isEditing, setIsEditing] = useState(false);
	const [tempDate, setTempDate] = useState('');
	const [saving, setSaving] = useState(false);
	const { disabled, setDisabled } = useDisabledContext();

	const startEditing = () => {
		setIsEditing(true);
		setTempDate(course.due_at ? course.due_at.slice(0, 16) : '');
	};

	const cancelEditing = () => {
		setIsEditing(false);
		setTempDate('');
	};

	const saveDueDate = async () => {
		if (!tempDate) return;

		setSaving(true);
		setDisabled(true);

		try {
			const response = await axios.post(
				route(API_ENDPOINTS.ADMIN.TRAINING.INDIVIDUAL_COURSE.DUE_DATE, {
					userId: userId,
					trainingCourse: course.id,
				}),
				{ due_date: tempDate }
			);

			customToast({
				title: response?.data?.message || 'Due date updated successfully.',
				variant: Variants.Success,
			});

			setIsEditing(false);
			onSaveSuccess();
		} catch (error: any) {
			customToast({
				title: error?.response?.data?.message || 'Failed to update due date.',
				variant: Variants.Danger,
			});
		} finally {
			setSaving(false);
			setDisabled(false);
		}
	};

	if (isEditing) {
		return (
			<div className='d-flex align-items-center gap-2'>
				<input
					type='datetime-local'
					value={tempDate}
					onChange={(e) => setTempDate(e.target.value)}
					className='form-control form-control-sm'
					style={{ width: '220px' }}
					required
				/>
				<button
					type='button'
					className='btn btn-success btn-sm'
					onClick={saveDueDate}
					disabled={saving || disabled || !tempDate}
				>
					<Icon icon={Icons.CHECK_CIRCLE} />
				</button>
				<button
					type='button'
					className='btn btn-secondary btn-sm'
					onClick={cancelEditing}
					disabled={saving || disabled}
					title='Cancel'
				>
					<span>✕</span>
				</button>
			</div>
		);
	}

	return (
		<div className='d-flex align-items-center gap-2'>
			<span>{formatDate(course.due_at) || '-'}</span>
			{canEditDueDate && (
				<button
					type='button'
					className='btn btn-link btn-sm p-0 ms-1'
					style={{ textDecoration: 'none' }}
					onClick={startEditing}
					disabled={disabled}
				>
					<Icon icon={Icons.EDIT} />
				</button>
			)}
		</div>
	);
}

function TrainingCourses() {
	const { fields, crud } = useLangContext();
	const { userId } = useParams();
	const { hasRoleAccess } = useAuthorisationContext();

	const [refreshOnChange, setRefreshOnChange] = useState(Date.now());

	const canEditDueDate =
		checkTheme(Themes.Toshiba) &&
		(hasRoleAccess(Roles.SUPER_ADMIN) || hasRoleAccess(Roles.PLATFORM_MANAGER));

	const getVariant = (status: string) => {
		switch (status) {
			case 'Completed':
				return Variants.Success;
			case 'Overdue':
				return Variants.Danger;
			case 'Outstanding':
				return Variants.Warning;
			default:
				return Variants.Primary;
		}
	};

	const colMapping = (data: CourseType) => [
		{
			id: 'name',
			label: StringHelpers.title(fields?.name || 'Name'),
			value: StringHelpers.title(data.name || '-'),
			sortable: true,
		},
		{
			id: 'category_name',
			label: StringHelpers.title(fields?.category || 'Category'),
			value: StringHelpers.title(data.category_name || '-'),
			sortable: true,
		},
		{
			id: 'due_at',
			label: StringHelpers.title(fields?.due_at || 'Due'),
			value: <DueDateCell course={data} canEditDueDate={canEditDueDate} userId={String(userId)} onSaveSuccess={() => setRefreshOnChange(Date.now())} />,
			sortable: true,
		},
		{
			id: 'admin_status',
			label: StringHelpers.title(fields?.status || 'Status'),
			value: data.admin_status ? (
				<Badge
					label={StringHelpers.title(data.admin_status)}
					variant={getVariant(data.admin_status)}
				/>
			) : (
				'-'
			),
			sortable: true,
		},
	];

	return (
		<DataTable<CourseType>
			title={StringHelpers.title(
				crud?.sub_titles?.training_courses || 'Training Courses'
			)}
			routeEndpoint={route(API_ENDPOINTS.ADMIN.USER.TRAINING_COURSES, {
				userId: String(userId),
			})}
			colMapping={colMapping}
			incrementPaginationButtons={true}
			prefix='user_view_courses'
			refreshOnChange={String(refreshOnChange)}
		/>
	);
}

export default TrainingCourses;